<script lang="ts">
  import { browser } from '$app/environment';
  import { goto } from '$app/navigation';
  import { page } from '$app/stores';
  import { preferencesDialog } from '$components/PreferencesModal.svelte';
  import { openPreferencesTab } from '$components/preferences/PreferencesTabs.svelte';
  import ClickableToast from '$components/toasts/ClickableToast.svelte';
  import type { LayoutPreference } from '$types/races';
  import { isSubscribed } from '$utils/auth';
  import { selectedRaceHasSP } from '$utils/race';
  import { activeSiteConfig, shortenBookmakerName } from '$utils/ui';
  import toast from 'svelte-french-toast';
  import TablerExternalLink from '~icons/tabler/external-link';
  import TablerLock from '~icons/tabler/lock';
  import TablerSortAscendingNumbers from '~icons/tabler/sort-ascending-numbers';
  import TablerSortDescendingNumbers from '~icons/tabler/sort-descending-numbers';

  export let computedLayoutPreferences: LayoutPreference[];
  export let betfairMarketTypes: ('win' | 'place')[];

  $: ({ layoutConfigStore, streamed } = $page.data);
  $: activeLayoutConfig = layoutConfigStore?.activeLayoutConfig;

  $: hoveredBookmaker = '';
  async function handleClick(
    bookmaker: string | null | undefined,
    marketType: 'win' | 'place' = 'win'
  ) {
    if (!bookmaker) {
      return;
    }
    if (bookmaker === 'Betfair') {
      if (marketType) {
        const marketId =
          streamed?.raceData?.data?.race.betfairMapping?.[marketType];
        if (!marketId) {
          return;
        }
        window.open(`https://betfair.com.au/dl/${marketId}`);
      }
    } else {
      const link = streamed?.raceData?.data?.race.links.find(
        (l) => l.bookmaker === bookmaker
      );
      if (link?.navLink) {
        if (!$isSubscribed) {
          // show toast to subscribe
          toast(ClickableToast, {
            icon: '🔒',
            duration: 5000,
            position: 'bottom-center',
            // @ts-ignore - svelte-french-toast doesn't support custom props
            props: {
              content: 'Subscribe to enable bookmaker links',
              subtitle: 'Open the relevant bookmaker event page with one click',
              onClick: async () => {
                await goto('/billing/subscribe/racing');
              }
            }
          });
          return;
        }

        // if the user is not subscribed, open the affiliate link if it exists
        window.open(
          !$isSubscribed && link.affiliateLink
            ? link.affiliateLink
            : link.navLink,
          '_blank'
        );
      }
    }
  }
</script>

<tr class="h-24">
  <th
    rowspan="2"
    class="relative z-10 bg-gray-50 px-6 py-5 text-left align-bottom text-sm font-medium uppercase tracking-wider text-gray-900 md:sticky md:left-0"
  >
    <div class="flex flex-row lg:pl-2">
      <span class="flex flex-1 self-center">Runner</span>
      {#if $activeLayoutConfig?.runnerSort === 'PRICE_ASC'}
        <button
          class="flex rounded-md p-1 hover:bg-gray-300/50"
          on:click={() => openPreferencesTab('Customise Layouts')}
        >
          <TablerSortAscendingNumbers class="flex-0 flex text-gray-500" />
        </button>
      {:else if $activeLayoutConfig?.runnerSort === 'PRICE_DESC'}
        <button
          class="flex rounded-md p-1 hover:bg-gray-300/50"
          on:click={preferencesDialog.open}
        >
          <TablerSortDescendingNumbers class="flex-0 flex text-gray-500" />
        </button>
      {/if}
    </div>
  </th>

  <!-- If there is a betfair sp we want to show it before any other columns -->
  {#if $selectedRaceHasSP}
    {#each betfairMarketTypes as marketType}
      <th
        on:click={() => handleClick('Betfair', marketType)}
        on:mouseenter={() => (hoveredBookmaker = 'Betfair')}
        on:mouseleave={() => (hoveredBookmaker = '')}
        class="bookmaker-betfair relative cursor-pointer py-1 text-center text-xs font-medium uppercase"
      >
        {#if hoveredBookmaker === 'Betfair' && streamed?.raceData?.data?.race.betfairMapping?.[marketType]}
          <div class="absolute bottom-0 right-0 opacity-40">
            <TablerExternalLink />
          </div>
        {:else if $activeLayoutConfig?.runnerPriceSortBookmaker === 'Betfair' && $activeLayoutConfig.runnerSort === 'PRICE_ASC'}
          <div class="absolute bottom-0 right-0 opacity-70">
            <TablerSortAscendingNumbers class="flex-0 flex" />
          </div>
        {:else if $activeLayoutConfig?.runnerPriceSortBookmaker === 'Betfair' && $activeLayoutConfig.runnerSort === 'PRICE_DESC'}
          <div class="absolute bottom-0 right-0 opacity-70">
            <TablerSortDescendingNumbers class="flex-0 flex" />
          </div>
        {/if}
        <div class="absolute bottom-0 left-0 opacity-70">
          <TablerLock width="12" height="12" />
        </div>
        <span class="[writing-mode:vertical-lr]">Betfair</span>
      </th>
    {/each}
  {/if}

  {#each computedLayoutPreferences as layoutPreference}
    {#if layoutPreference.type === 'BEST_PRICE_PLACE' || layoutPreference.type === 'BEST_PRICE_WIN'}
      <th
        colspan="4"
        class="relative cursor-pointer bg-gradient-to-br from-indigo-100 to-indigo-50 px-6 py-5 text-center align-bottom text-sm font-medium uppercase
        tracking-wider"
      >
        {#if layoutPreference.locked}
          <div class="absolute bottom-0 left-0 opacity-70">
            <TablerLock width="12" height="12" />
          </div>
        {/if}
        <span>Best Price</span>
      </th>
    {:else if layoutPreference.type === 'SPACER'}
      <th class="w-4 border-0 bg-white" />
    {:else if layoutPreference.type === 'TEXT'}
      <th
        class="relative cursor-pointer bg-slate-50 py-1 text-center text-xs font-medium uppercase"
      >
        <span class="[writing-mode:vertical-lr]"
          >{layoutPreference?.name ?? 'Custom'}
        </span>
      </th>
    {:else if (($selectedRaceHasSP && layoutPreference.bookmaker !== 'Betfair') || !$selectedRaceHasSP || layoutPreference.type === 'FIXED_WIN_FLUCS' || layoutPreference.type === 'FIXED_PLACE_FLUCS') && layoutPreference.bookmaker}
      <!-- If we have a betfair sp, don't show any regular betfair columns -->
      <th
        on:click={() => handleClick(layoutPreference.bookmaker)}
        on:mouseenter={() =>
          (hoveredBookmaker = layoutPreference.bookmaker ?? '')}
        on:mouseleave={() => (hoveredBookmaker = '')}
        class="bookmaker-{layoutPreference.bookmaker
          .toLowerCase()
          .replace(
            ' ',
            ''
          )} relative cursor-pointer py-1 text-center text-xs font-medium uppercase"
      >
        {#if hoveredBookmaker === layoutPreference.bookmaker && (streamed?.raceData?.data?.race.links.find((l) => l.bookmaker === layoutPreference.bookmaker) || layoutPreference.bookmaker === 'Betfair')}
          <div class="absolute bottom-0 right-0 opacity-40">
            <TablerExternalLink />
          </div>
        {:else if $activeLayoutConfig?.runnerPriceSortBookmaker === layoutPreference.bookmaker && $activeLayoutConfig?.runnerSort === 'PRICE_ASC'}
          <div class="absolute bottom-0 right-0 opacity-70">
            <TablerSortAscendingNumbers class="flex-0 flex" />
          </div>
        {:else if $activeLayoutConfig?.runnerPriceSortBookmaker === layoutPreference.bookmaker && $activeLayoutConfig?.runnerSort === 'PRICE_DESC'}
          <div class="absolute bottom-0 right-0 opacity-70">
            <TablerSortDescendingNumbers class="flex-0 flex" />
          </div>
        {/if}
        {#if layoutPreference.locked}
          <div class="absolute bottom-0 left-0 opacity-70">
            <TablerLock width="12" height="12" />
          </div>
        {/if}
        <span class="[writing-mode:vertical-lr]">
          {shortenBookmakerName(
            layoutPreference.bookmaker
          )}{layoutPreference.bookmaker === 'Betfair' &&
          $activeSiteConfig.betfairReductionFactor &&
          browser &&
          $isSubscribed
            ? '*'
            : ''}
        </span>
      </th>
    {/if}
  {/each}
</tr>
